// MIA Chat Configuration
// This file controls various settings for the MIA Chat functionality

export interface MiaChatConfig {
  // Mock Mode Settings
  mockMode: {
    enabled: boolean;
    delay: number; // Simulated API delay in milliseconds
    showMockIndicator: boolean; // Show visual indicator when mock mode is active
  };

  // API Settings
  api: {
    baseUrl: string;
    timeout: number;
    retryAttempts: number;
  };

  // Development Settings
  development: {
    logApiCalls: boolean;
    logMockResponses: boolean;
    enableErrorSimulation: boolean;
  };
}

// Environment variable helpers
const getEnvBool = (key: string, defaultValue: boolean): boolean => {
  const value = process.env[key];
  if (value === undefined) return defaultValue;
  return value.toLowerCase() === 'true';
};

const getEnvNumber = (key: string, defaultValue: number): number => {
  const value = process.env[key];
  if (value === undefined) return defaultValue;
  const num = parseInt(value, 10);
  return isNaN(num) ? defaultValue : num;
};

const getEnvString = (key: string, defaultValue: string): string => {
  return process.env[key] || defaultValue;
};

const isProductionEnvironment = (): boolean => {
  const nodeEnv = process.env.NODE_ENV;
  if (typeof nodeEnv === 'string' && nodeEnv.toLowerCase() === 'production') {
    return true;
  }

  const reactAppNodeEnv = process.env.REACT_APP_NODE_ENV;
  if (
    typeof reactAppNodeEnv === 'string' &&
    reactAppNodeEnv.toLowerCase() === 'production'
  ) {
    return true;
  }

  const envOverride =
    process.env.REACT_APP_RUNTIME_ENVIRONMENT ||
    process.env.REACT_APP_DEPLOYMENT_ENV ||
    process.env.REACT_APP_DEPLOY_ENV ||
    process.env.REACT_APP_STAGE ||
    process.env.REACT_APP_ENV;

  if (typeof envOverride === 'string' && envOverride.toLowerCase() === 'production') {
    return true;
  }

  return false;
};

// Default configuration
export const defaultMiaChatConfig: MiaChatConfig = {
  mockMode: {
    enabled: false, // Default to mock mode for development
    delay: 5000, // 5 seconds delay for email generation
    showMockIndicator: true,
  },
  api: {
    baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:3000',
    timeout: 30000,
    retryAttempts: 3,
  },
  development: {
    logApiCalls: true,
    logMockResponses: true,
    enableErrorSimulation: false,
  },
};

// Environment-specific overrides
const getEnvironmentConfig = (): Partial<MiaChatConfig> => {
  const env = process.env.NODE_ENV;

  switch (env) {
    case 'production':
      return {
        mockMode: {
          enabled: false,
          delay: 0,
          showMockIndicator: false,
        },
        development: {
          logApiCalls: false,
          logMockResponses: false,
          enableErrorSimulation: false,
        },
      };
    case 'development':
      return {
        mockMode: {
          enabled: false,
          delay: 5000, // 5 seconds delay for email generation
          showMockIndicator: true,
        },
        development: {
          logApiCalls: true,
          logMockResponses: true,
          enableErrorSimulation: false,
        },
      };
    default:
      return {};
  }
};

// Environment variable overrides
const getEnvironmentVariableConfig = (): Partial<MiaChatConfig> => {
  const isProd = isProductionEnvironment();
  const mockModeEnabledFromEnv = getEnvBool(
    'REACT_APP_MIA_CHAT_MOCK_MODE',
    defaultMiaChatConfig.mockMode.enabled,
  );
  const showMockIndicatorFromEnv = getEnvBool(
    'REACT_APP_MIA_CHAT_SHOW_MOCK_INDICATOR',
    defaultMiaChatConfig.mockMode.showMockIndicator,
  );

  return {
    mockMode: {
      enabled: isProd ? false : mockModeEnabledFromEnv,
      delay: getEnvNumber('REACT_APP_MIA_CHAT_MOCK_DELAY', defaultMiaChatConfig.mockMode.delay),
      showMockIndicator: isProd ? false : showMockIndicatorFromEnv,
    },
    api: {
      baseUrl: getEnvString('REACT_APP_API_URL', defaultMiaChatConfig.api.baseUrl),
      timeout: getEnvNumber('REACT_APP_MIA_CHAT_API_TIMEOUT', defaultMiaChatConfig.api.timeout),
      retryAttempts: getEnvNumber('REACT_APP_MIA_CHAT_API_RETRY_ATTEMPTS', defaultMiaChatConfig.api.retryAttempts),
    },
    development: {
      logApiCalls: getEnvBool('REACT_APP_MIA_CHAT_LOG_API_CALLS', defaultMiaChatConfig.development.logApiCalls),
      logMockResponses: getEnvBool('REACT_APP_MIA_CHAT_LOG_MOCK_RESPONSES', defaultMiaChatConfig.development.logMockResponses),
      enableErrorSimulation: getEnvBool('REACT_APP_MIA_CHAT_ENABLE_ERROR_SIMULATION', defaultMiaChatConfig.development.enableErrorSimulation),
    },
  };
};

// Final configuration with all overrides
export const miaChatConfig: MiaChatConfig = {
  ...defaultMiaChatConfig,
  ...getEnvironmentConfig(),
  ...getEnvironmentVariableConfig(),
};

// Configuration utility functions
export const isMockModeEnabled = (): boolean => {
  return miaChatConfig.mockMode.enabled;
};

export const getMockDelay = (): number => {
  return miaChatConfig.mockMode.delay;
};

export const shouldShowMockIndicator = (): boolean => {
  if (isProductionEnvironment()) {
    return false;
  }
  return miaChatConfig.mockMode.showMockIndicator;
};

export const shouldLogApiCalls = (): boolean => {
  return miaChatConfig.development.logApiCalls;
};

export const shouldLogMockResponses = (): boolean => {
  return miaChatConfig.development.logMockResponses;
};

// Configuration update functions (for runtime changes)
export const updateMockMode = (enabled: boolean): void => {
  if (isProductionEnvironment()) {
    if (shouldLogApiCalls()) {
      console.warn('MIA Chat Mock Mode toggle is disabled in production.');
    }
    miaChatConfig.mockMode.enabled = false;
    return;
  }

  miaChatConfig.mockMode.enabled = enabled;

  // Persist to localStorage for user preference
  if (typeof window !== 'undefined') {
    localStorage.setItem('miaChat_mockMode', JSON.stringify(enabled));
  }

  if (shouldLogApiCalls()) {
    console.log(`MIA Chat Mock Mode: ${enabled ? 'ENABLED' : 'DISABLED'}`);
  }
};

export const updateMockDelay = (delay: number): void => {
  if (isProductionEnvironment()) {
    if (shouldLogApiCalls()) {
      console.warn('MIA Chat Mock Delay update is ignored in production.');
    }
    miaChatConfig.mockMode.delay = 0;
    return;
  }

  miaChatConfig.mockMode.delay = Math.max(0, delay);

  if (shouldLogApiCalls()) {
    console.log(`MIA Chat Mock Delay: ${delay}ms`);
  }
};

// Load user preferences from localStorage
export const loadUserPreferences = (): void => {
  if (isProductionEnvironment()) {
    // Do not override production configuration with local preferences
    return;
  }

  if (typeof window !== 'undefined') {
    try {
      // Clear any existing mock mode preference to ensure default behavior
      localStorage.removeItem('miaChat_mockMode');
      // Force mock mode to be disabled by default
      updateMockMode(false);
    } catch (error) {
      console.warn('Failed to load MIA Chat user preferences:', error);
    }
  }
};

// Initialize configuration
loadUserPreferences();

// Log initial configuration
if (shouldLogApiCalls()) {
  console.log('🔧 MIA Chat Configuration Loaded:', {
    mockMode: miaChatConfig.mockMode.enabled,
    delay: miaChatConfig.mockMode.delay,
    environment: process.env.NODE_ENV,
    apiUrl: miaChatConfig.api.baseUrl,
  });
}
